import React from 'react';
import { 
    Box, 
    Typography, 
    Card, 
    Divider, 
    Grid, 
    Container,
    Chip,
    Avatar,
    Stack,
    ThemeProvider, 
    createTheme,
    useTheme,
    useMediaQuery,
    CssBaseline 
} from "@mui/material";
import { 
    Lightbulb, 
    GppGood, 
    Handshake, 
    QueryBuilder, 
    Groups, 
    TrendingUp, 
    ArrowForwardIos,
    Phone
} from "@mui/icons-material";

// --- 1. THEME DEFINITION (SHARPER BORDERS) ---
const theme = createTheme({
  palette: {
    primary: {
      main: '#0056b3', // A slightly darker, more professional blue
      light: '#e3f2fd',
      dark: '#003f80',
    },
    secondary: {
      main: '#607d8b', // Professional grey-blue
    },
    background: {
      default: '#f8f9fa', 
      paper: '#ffffff',
    },
    text: {
      primary: '#212529', 
      secondary: '#495057',
    }
  },
  typography: {
    fontFamily: 'Roboto, sans-serif',
    h2: {
      fontSize: '2.8rem', // Slightly larger for impact
      fontWeight: 700,
      '@media (min-width:600px)': {
        fontSize: '3.8rem',
      },
    },
    h4: {
        fontWeight: 600,
    },
    h6: {
        fontWeight: 600,
        fontSize: '1.2rem',
    }
  },
  shape: {
    borderRadius: 3, // Significantly reduced for a sharper, more professional look
  }
});


// --- 2. FOOTER COMPONENT ---
const Footer = () => (
  <Box component="footer" sx={{ bgcolor: "background.paper", borderTop: `1px solid ${theme.palette.divider}`, py: 4 }}>
    <Container maxWidth="lg">
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} flexWrap="wrap">
        <Typography variant="body2" color="text.secondary">
          &copy; {new Date().getFullYear()} Brothers Consulting. All rights reserved.
        </Typography>
        <Stack direction="row" spacing={3} divider={<Divider orientation="vertical" flexItem />}>
          <Typography variant="body2" color="text.secondary" sx={{ '&:hover': { color: 'primary.main', cursor: 'pointer' } }}>
            Services
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ '&:hover': { color: 'primary.main', cursor: 'pointer' } }}>
            Careers
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ '&:hover': { color: 'primary.main', cursor: 'pointer' } }}>
            Privacy
          </Typography>
        </Stack>
      </Stack>
    </Container>
  </Box>
);

// --- 3. MAIN CONTENT (AboutUs) ---
function AboutUsContent() {
  const localTheme = useTheme();
  const isMobile = useMediaQuery(localTheme.breakpoints.down('md'));

  // UPDATED B2B VALUES (English)
  const values = [
    { icon: <Lightbulb fontSize="large"/>, label: "Innovation & Strategy", description: "Creative solutions and tailored growth plans for your business." },
    { icon: <GppGood fontSize="large"/>, label: "Quality & Integrity", description: "Excellence and transparency in every project delivery." },
    { icon: <Handshake fontSize="large"/>, label: "Strategic Partnerships", description: "Building trusted relationships for your sustained success." },
    { icon: <QueryBuilder fontSize="large"/>, label: "Efficiency & Agility", description: "Optimizing processes for rapid and effective outcomes." },
    { icon: <Groups fontSize="large"/>, label: "Multidisciplinary Expertise", description: "Certified team with deep knowledge across various sectors." },
    { icon: <TrendingUp fontSize="large"/>, label: "Growth-Oriented Focus", description: "Your return on investment is our ultimate measure of success." },
  ];

  return (
    <>
      <Box sx={{ bgcolor: "background.default", minHeight: "100vh", py: { xs: 4, md: 8 } }}>
        <Container maxWidth="lg">
          <Card
            elevation={isMobile ? 2 : 12}
            sx={{
              p: { xs: 3, md: 8 },
              backgroundColor: "background.paper",
              borderRadius: localTheme.shape.borderRadius,
              boxShadow: `0 15px 40px rgba(0, 0, 0, 0.1)`,
              overflow: "hidden",
              position: "relative",
              borderTop: `6px solid ${localTheme.palette.primary.main}`, 
            }}
          >
            {/* Title Section */}
            <Box sx={{ textAlign: "center", mb: { xs: 4, md: 8 } }}>
              <Chip 
                label="WHO WE ARE" 
                color="primary" 
                size="small"
                sx={{ 
                    mb: 2, 
                    fontWeight: 600, 
                    letterSpacing: 1.5, 
                    fontSize: '0.8rem',
                    backgroundColor: `${localTheme.palette.primary.main}15`, 
                    color: 'primary.main',
                    py: 1.5,
                    px: 1,
                    borderRadius: localTheme.shape.borderRadius / 2 // Chip also sharper
                }}
              />
              <Typography
                variant="h2"
                sx={{
                  color: "text.primary",
                }}
              >
                Brothers Consulting: Your B2B Partner
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ mt: 1 }}
              >
                Experts in strategy, technology, and enterprise solutions.
              </Typography>
            </Box>

            {/* Main Content & Image */}
            <Grid container spacing={5} alignItems="center" sx={{ mb: { xs: 8, md: 12 } }}>
              <Grid item xs={12} md={6}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 3, 
                    color: "primary.dark", 
                    lineHeight: 1.6,
                  }}
                >
                  Brothers Consulting is dedicated to providing strategic and technological solutions to drive business growth and efficiency for enterprises.
                </Typography>
                
                <Typography 
                  variant="body1" 
                  sx={{ 
                    mb: 3, 
                    color: "text.secondary", 
                    lineHeight: 1.7,
                    fontSize: "1.05rem"
                  }}
                >
                  With a decade of experience in the corporate market, our team is passionate about transforming challenges into opportunities. We combine agile methodologies with deep industry knowledge to deliver measurable and sustainable results.
                </Typography>
                
                <Box sx={{ 
                  backgroundColor: localTheme.palette.primary.light, 
                  p: 3, 
                  borderRadius: localTheme.shape.borderRadius,
                  borderLeft: `5px solid ${localTheme.palette.primary.main}`,
                  mt: 4
                }}>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: "text.primary", 
                      fontStyle: "italic",
                      fontWeight: 500,
                      lineHeight: 1.6
                    }}
                  >
                    "Our focus is your strategy. We work hand-in-hand to ensure your company not only keeps pace but leads the market through innovation and operational excellence."
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ my: { xs: 4, md: 6 } }} />

            {/* Values Section (B2B Values) */}
            <Box sx={{ p: { xs: 0, md: 2 } }}>
              <Box sx={{ textAlign: "center", mb: { xs: 4, md: 6 } }}>
                <Chip 
                  label="OUR CORE PRINCIPLES" 
                  color="primary" 
                  size="small"
                  sx={{ 
                    mb: 2, 
                    fontWeight: 600, 
                    letterSpacing: 1.5,
                    fontSize: '0.8rem',
                    backgroundColor: `${localTheme.palette.primary.main}15`,
                    color: 'primary.main',
                    py: 1.5,
                    px: 1,
                    borderRadius: localTheme.shape.borderRadius / 2 // Chip also sharper
                  }}
                />
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: "bold",
                    mb: 1,
                    color: "text.primary",
                  }}
                >
                  The Pillars of Our Consulting Approach
                </Typography>
              </Box>

              <Grid container spacing={4}>
                {values.map((value, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card
                      sx={{
                        p: 3,
                        textAlign: "center",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        height: '100%',
                        background: localTheme.palette.background.default, 
                        border: `1px solid ${localTheme.palette.divider}`,
                        boxShadow: "0 4px 15px rgba(0, 0, 0, 0.05)",
                        borderRadius: localTheme.shape.borderRadius, 
                        transition: "all 0.3s ease",
                        "&:hover": { 
                          transform: "translateY(-4px)",
                          boxShadow: `0 10px 20px ${localTheme.palette.primary.main}1a`,
                        }
                      }}
                    >
                      <Avatar
                        sx={{
                          backgroundColor: "primary.main",
                          color: "white",
                          width: 64,
                          height: 64,
                          mb: 2,
                          boxShadow: `0 4px 10px ${localTheme.palette.primary.main}40`
                        }}
                      >
                        {value.icon}
                      </Avatar>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          mb: 1,
                          color: "primary.main",
                        }}
                      >
                        {value.label}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          lineHeight: 1.6,
                        }}
                      >
                        {value.description}
                      </Typography>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
            
            <Divider sx={{ my: { xs: 4, md: 6 } }} />

            {/* Final Call-to-Action (CTA) */}
            <Box sx={{ 
              textAlign: "center", 
              p: { xs: 3, md: 5 },
              borderRadius: localTheme.shape.borderRadius,
              background: `linear-gradient(135deg, ${localTheme.palette.primary.main}, ${localTheme.palette.primary.dark})`,
              color: 'white',
              boxShadow: `0 10px 25px ${localTheme.palette.primary.main}40`
            }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: "bold",
                  mb: 2,
                }}
              >
                Ready to Elevate Your Business Strategy?
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  mb: 4,
                  maxWidth: 700,
                  mx: "auto",
                  color: 'rgba(255, 255, 255, 0.85)'
                }}
              >
                Connect with our experienced consultants and discover how we can optimize your operations and accelerate your growth.
              </Typography>
              <Chip
                label={
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Phone />
                        <Typography variant="button">Schedule a Consultation: (800) 555-0199</Typography>
                        <ArrowForwardIos sx={{ width: 14, height: 14 }} />
                    </Stack>
                }
                component="a"
                href="tel:18005550199" // US toll-free format
                clickable
                sx={{
                    bgcolor: 'white',
                    color: localTheme.palette.primary.main,
                    p: 2.5,
                    fontSize: '1rem',
                    fontWeight: 700,
                    borderRadius: localTheme.shape.borderRadius / 2, 
                    transition: 'all 0.3s',
                    '&:hover': {
                        bgcolor: '#f0f0f0',
                        transform: 'scale(1.02)'
                    }
                }}
              />
            </Box>
          </Card>
        </Container>
      </Box>
    </>
  );
}

// 4. MAIN WRAPPER 
const App = () => (
    <ThemeProvider theme={theme}>
        <CssBaseline />
        <AboutUsContent />
        <Footer />
    </ThemeProvider>
);

export default App;